import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { NeuButton } from "../../components/neu/Neu";
import { useLightMeter } from "../../hooks/useLightMeter";
import { useScreenBoost } from "../../hooks/useScreenBoost";
import { ReadinessChecklist } from "./ReadinessChecklist";
import { CaptureGateView } from "./CaptureGateView";
import { OffDayAllowedBanner } from "./OffDayAllowedBanner";
import { CameraStage } from "./CameraStage";
import { CaptureStatus } from "./CaptureStatus";
import { LivenessPanel } from "./LivenessPanel";
import { gateOf, isOffDayAllowed } from "./captureGates";

// Full-screen capture sheet: fills the phone on mobile, phone-sized card on desktop.
export const FaceCapture = ({ open, phase, message, similarity, onCapture, onClose, actionLabel, camera, readiness, checking, liveness, onLivenessDone, onLivenessError, onLivenessRetry, onQrCode, geoError }) => {
  const { t } = useTranslation();
  const { videoRef, error, live } = camera;
  useScreenBoost(open);
  const { tooDark, tooBright } = useLightMeter(videoRef, open);

  if (!open) return null;

  const gate = gateOf(readiness, onQrCode);
  // لا تُفتح الكاميرا قبل وصول نتيجة الفحص ولا عند وجود بوابة مقفلة
  const waiting = !readiness || checking;
  const offDayAllowed = isOffDayAllowed(readiness);
  const awsLiveness = Boolean(liveness?.enabled && readiness?.allowed);

  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-background sm:items-center sm:bg-black/70" data-testid="face-capture-overlay">
      <div
        data-testid="face-capture-dialog"
        className="flex h-[100dvh] w-full max-w-md flex-col bg-background px-4 text-foreground sm:h-[94dvh] sm:max-h-[860px] sm:animate-pop sm:rounded-[32px] sm:shadow-2xl"
        style={{ paddingTop: "max(env(safe-area-inset-top),0.75rem)", paddingBottom: "max(env(safe-area-inset-bottom),0.75rem)" }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3">
          <p className="truncate text-xs font-bold text-slate-400">{t("check.capture")}</p>
          <button
            data-testid="face-capture-close"
            onClick={onClose}
            aria-label={t("app.close")}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 shadow-neu-sm active:shadow-neu-in"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {waiting || gate.kind ? (
          <CaptureGateView waiting={waiting} gate={gate} geoError={geoError} onClose={onClose} onQrCode={onQrCode} />
        ) : awsLiveness ? (
          <LivenessPanel
            liveness={liveness}
            phase={phase}
            message={message}
            similarity={similarity}
            readiness={readiness}
            checking={checking}
            onDone={onLivenessDone}
            onError={onLivenessError}
            onCancel={onClose}
            onRetry={onLivenessRetry}
          />
        ) : (
          <>
            {offDayAllowed ? <OffDayAllowedBanner /> : null}
            <CameraStage videoRef={videoRef} phase={phase} error={error} />

            <div className="max-h-[36dvh] shrink-0 overflow-y-auto">
              <CaptureStatus
                phase={phase}
                message={message}
                similarity={similarity}
                tooDark={tooDark}
                tooBright={tooBright}
              />
              <ReadinessChecklist readiness={readiness} loading={checking} />
            </div>

            <NeuButton
              variant="primary"
              data-testid="face-capture-confirm"
              disabled={!live || phase === "verifying" || checking || !readiness?.allowed}
              onClick={onCapture}
              className="mt-3 w-full shrink-0 py-4 text-base"
            >
              {phase === "verifying" ? t("check.verifying") : readiness?.allowed ? actionLabel : t("readiness.locked")}
            </NeuButton>
          </>
        )}
      </div>
    </div>
  );
};
